import React from "react";
import { profile } from "@/lib/data";
import { SectionHeader } from "@/components/About";
import {
    Mail,
    Linkedin,
    Github,
    Twitter,
    Instagram,
    BookOpen,
    Globe,
    Download,
    ArrowUpRight,
} from "lucide-react";

const channels = [
    {
        key: "email",
        label: "Email",
        value: profile.email,
        href: `mailto:${profile.email}`,
        Icon: Mail,
        primary: true,
    },
    {
        key: "linkedin",
        label: "LinkedIn",
        value: "linkedin.com/in/manoj-suthar",
        href: profile.socials.linkedin,
        Icon: Linkedin,
    },
    {
        key: "github",
        label: "GitHub",
        value: "github.com/manojsuthar123",
        href: profile.socials.github,
        Icon: Github,
    },
    {
        key: "twitter",
        label: "Twitter / X",
        value: "@manoj__official",
        href: profile.socials.twitter,
        Icon: Twitter,
    },
    {
        key: "medium",
        label: "Medium",
        value: "manoj-suthar.medium.com",
        href: profile.socials.medium,
        Icon: BookOpen,
    },
    {
        key: "instagram",
        label: "Instagram",
        value: "@manojsuthar07",
        href: profile.socials.instagram,
        Icon: Instagram,
    },
    {
        key: "website",
        label: "Website",
        value: "manojsuthar.com",
        href: profile.socials.website,
        Icon: Globe,
    },
];

export default function Contact() {
    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="relative py-24 md:py-32 border-t border-term-green/15"
        >
            <SectionHeader
                index="06"
                label="get in touch"
                command="echo $EMAIL | xargs open"
            />

            <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-14 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7 space-y-8">
                    <h2 className="font-mono text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.0] text-ink-primary">
                        Let&apos;s build
                        <br />
                        <span className="text-term-green">something</span>
                        <br />
                        meaningful<span className="text-term-green">.</span>
                    </h2>
                    <p className="font-display text-base md:text-lg text-ink-secondary max-w-xl leading-relaxed">
                        Open to senior backend, platform engineering and GenAI roles —
                        full-time or consulting. The fastest way to reach me is email or
                        LinkedIn.
                    </p>

                    {/* Massive email */}
                    <a
                        href={`mailto:${profile.email}`}
                        data-testid="contact-email-large"
                        className="group block break-all font-mono text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-term-green hover:text-term-green-hi transition-colors"
                    >
                        <span className="text-ink-tertiary">$ </span>
                        {profile.email}
                        <span className="cursor-blink ml-1">▍</span>
                    </a>

                    <div className="flex flex-wrap items-center gap-3 pt-2">
                        <a
                            href={`mailto:${profile.email}`}
                            data-testid="contact-email-btn"
                            className="inline-flex items-center gap-2 px-5 py-3 bg-term-green text-bg-base font-mono text-sm font-semibold uppercase tracking-[0.14em] hover:bg-term-green-hi active:translate-y-[2px] transition-all"
                        >
                            <Mail className="w-4 h-4" />
                            send_email
                        </a>
                        <a
                            href={profile.resumeUrl}
                            download
                            data-testid="contact-resume-download"
                            className="inline-flex items-center gap-2 px-5 py-3 border border-term-green/50 text-term-green font-mono text-sm font-semibold uppercase tracking-[0.14em] hover:bg-term-green hover:text-bg-base active:translate-y-[2px] transition-all"
                        >
                            <Download className="w-4 h-4" />
                            resume.pdf
                        </a>
                    </div>
                </div>

                {/* Social grid */}
                <div className="lg:col-span-5">
                    <div
                        data-testid="contact-links"
                        className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-term-green/15 border border-term-green/20"
                    >
                        {channels.map((c) => (
                            <a
                                key={c.key}
                                href={c.href}
                                target={c.key === "email" ? "_self" : "_blank"}
                                rel="noreferrer"
                                data-testid={`contact-link-${c.key}`}
                                className="group bg-bg-base p-5 hover:bg-bg-surface transition-colors flex items-center justify-between gap-4"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <span className="w-9 h-9 grid place-items-center border border-term-green/30 group-hover:bg-term-green group-hover:text-bg-base text-term-green transition-colors">
                                        <c.Icon className="w-4 h-4" />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-term-green">
                                            // {c.label.toLowerCase()}
                                        </div>
                                        <div className="font-mono text-sm text-ink-primary truncate">
                                            {c.value}
                                        </div>
                                    </div>
                                </div>
                                <ArrowUpRight className="w-4 h-4 text-ink-tertiary group-hover:text-term-green group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
